<template>
    <div class="max-w-(--breakpoint-md) flex flex-col gap-4">
        <page-header>Calendly</page-header>

        <vx-expandable-card>
            <template v-slot:header> Calendly Integration </template>
            <template v-slot:headerRight>
                <div v-if="connectionStatus === 'loading'">
                    <vx-badge color="slate"> Loading </vx-badge>
                </div>
                <div v-else-if="connectionStatus === 'connected'">
                    <vx-badge color="success"> Connected </vx-badge>
                </div>
                <div v-else-if="connectionStatus === 'broken'">
                    <vx-badge color="orange"> Needs reconnection </vx-badge>
                </div>
                <div v-else>
                    <vx-badge color="slate"> Not connected </vx-badge>
                </div>
            </template>

            <template v-slot:content>
                <div v-if="!hasPermission" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                    Only the team owner can configure integrations.
                </div>
                <div v-else-if="connectionStatus === 'connected'" class="flex flex-col gap-6 mt-3">
                    <div class="mb-4">Your Calendly account is connected to Voxie.</div>

                    <div class="mb-4">
                        Click the button below to disconnect your Calendly account from your Voxie team.
                    </div>

                    <vx-button
                        color="danger"
                        size="lg"
                        @click="disconnectCalendly"
                        :disabled="disconnecting"
                        data-test="disconnect-button"
                    >
                        {{ disconnecting ? 'Disconnecting...' : 'Disconnect Calendly account' }}
                    </vx-button>
                </div>
                <div v-else-if="connectionStatus === 'broken'" class="flex flex-col gap-6 mt-3">
                    <div class="mb-4">
                        Your Calendly account needs to be reconnected. This can happen if the connection token has
                        expired or if there are changes in your Calendly account.
                    </div>

                    <vx-button size="lg" :href="calendlyConnectUrl"> Connect Calendly account </vx-button>
                </div>
                <div v-else-if="connectionStatus === 'not_connected'" class="flex flex-col gap-6 mt-3">
                    <div class="mb-4">
                        Click the button below to connect your Calendly account with your Voxie team. Note that you must
                        have "Admin" or "Owner" permissions in the Calendly account you wish to connect.
                    </div>

                    <vx-button size="lg" :href="calendlyConnectUrl"> Connect Calendly account </vx-button>
                </div>
            </template>
        </vx-expandable-card>

        <calendly-documentation />

        <integration-logs-card v-if="FEATURES.integration_logs" vendor="calendly" />

        <calendly-groups-mapping
            :ownsTeam="hasPermission"
            :isConnected="connectionStatus === 'connected'"
        ></calendly-groups-mapping>
    </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { FEATURES } from '~/provides/features';
import { VxExpandableCard, VxButton, VxBadge, VxSwal } from '@voxie/frontend-components';
import PageHeader from '~/components/general/PageHeader.vue';
import CalendlyDocumentation from './calendly/CalendlyDocumentation.vue';
import IntegrationLogsCard from './IntegrationLogsCard.vue';
import CalendlyGroupsMapping from './CalendlyGroupsMapping.vue';
import teamsClient from '~/services/teamsClient';
import { useToasted } from '~/composables/useToasted';

const props = defineProps({
    ownsTeam: {
        type: Boolean,
        default: false,
    },
});

const toasted = useToasted();
const connectionStatus = ref('loading');
const disconnecting = ref(false);

const getConnactionStatus = async () => {
    try {
        const response = await teamsClient.getCalendlyConnectionStatus(Spark.state.currentTeam.id);
        connectionStatus.value = response.data.status;
    } catch (error) {
        console.error(error);
        connectionStatus.value = 'not_connected';
    }
};

onMounted(async () => {
    getConnactionStatus();
});

const hasPermission = computed(() => props.ownsTeam);

const calendlyConnectUrl = computed(() => {
    return `/teams/${Spark.state.currentTeam.id}/integrations/calendly/oauth/initiate`;
});

const disconnectCalendly = () => {
    VxSwal.confirmation(
        'Disconnect Calendly',
        'Are you sure you want to disconnect your Calendly account? This action cannot be undone.',
        'danger',
        async () => {
            disconnecting.value = true;
            try {
                await teamsClient.disconnectCalendly(Spark.state.currentTeam.id);
                connectionStatus.value = 'not_connected';
                toasted.success('Calendly account disconnected successfully.');
            } catch (error) {
                console.error(error);
                toasted.error('Failed to disconnect Calendly account. Please try again.');
            } finally {
                disconnecting.value = false;
            }
        }
    );
};
</script>
